/**
 * Routes Notion editor link actions to the shared link URL popup.
 * Requires registerEditorLinkPickerHost() from account main.js.
 */
import { registerEditorLinkPickerHost } from "@components/TiptapEditor/notionEditorLinkPickerHost.js";
import { removeAllLinkPopupDomNodes } from "@components/TiptapEditor/linkInputPopup.js";
import {
  bindBubbleLinkButtonHandler,
  bindBubbleMenuWatcher,
  handleEditorLinkAnchorClick,
  injectBubbleLinkButton,
  resolveEditorFromProseMirror,
  resolveFocusedNotionEditor,
} from "./notionEditorLinkPatchUtils.js";

registerEditorLinkPickerHost();

(function initNotionEditorLinkPatch() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  if (window.__exiqNotionEditorLinkPatchInitialized) {
    return;
  }
  window.__exiqNotionEditorLinkPatchInitialized = true;

  const PATCH_FLAG = "__exiqNotionEditorLinkPatchApplied";
  const hijackedPopups = new WeakSet();

  function createLinkOnSave(editor) {
    return (href) => {
      if (href) {
        window.__exiqApplyEditorLink?.(editor, href, { displayText: "link" });
        return;
      }
      window.__exiqUnsetEditorLink?.(editor);
    };
  }

  function openLinkForEditor(editor, detail = {}) {
    if (!editor?.view || typeof window.__exiqOpenEditorLinkPopup !== "function") {
      return;
    }

    window.__exiqOpenEditorLinkPopup(editor, {
      initialUrl: detail.initialUrl ?? editor.getAttributes("link")?.href ?? "",
      allowUnset: detail.allowUnset !== false,
      onSave: createLinkOnSave(editor),
    });
  }

  function handleLinkModalRequest(detail) {
    if (!detail?.editor?.view) {
      return;
    }

    window.setTimeout(() => openLinkForEditor(detail.editor, detail), 0);
  }

  function hijackLegacySlashLinkPopup(popupNode) {
    if (!(popupNode instanceof HTMLElement) || hijackedPopups.has(popupNode)) {
      return;
    }
    if (popupNode.dataset.exiqManagedLinkPopup === "true") {
      return;
    }

    hijackedPopups.add(popupNode);
    popupNode.closest("[data-tippy-root]")?.remove();
    removeAllLinkPopupDomNodes();

    const editor = resolveFocusedNotionEditor();
    if (!editor) {
      return;
    }

    openLinkForEditor(editor);
  }

  function watchForLegacyLinkPopups(root) {
    root?.querySelectorAll?.(".slash-link-input-popup:not([data-exiq-managed-link-popup='true'])")?.forEach((popupNode) => {
      hijackLegacySlashLinkPopup(popupNode);
    });
  }

  function patchBubbleMenu(shadowRoot) {
    injectBubbleLinkButton(shadowRoot, openLinkForEditor);
  }

  window.__exiqOpenLinkModal = handleLinkModalRequest;
  window.__exiqPatchNotionEditorBubble = patchBubbleMenu;

  document.addEventListener(
    "open-link-modal",
    (event) => {
      const detail = event.detail || {};
      if (!detail.editor?.view) {
        return;
      }
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleLinkModalRequest(detail);
    },
    true
  );

  function bindEditorDom(editorDom) {
    if (!editorDom || editorDom[PATCH_FLAG]) {
      return;
    }

    editorDom[PATCH_FLAG] = true;
    editorDom.addEventListener("focusin", () => resolveEditorFromProseMirror(editorDom), true);
    editorDom.addEventListener(
      "click",
      (event) => handleEditorLinkAnchorClick(event, editorDom, openLinkForEditor),
      true
    );
  }

  function scanForEditorRoots(root) {
    root?.querySelectorAll?.(".ProseMirror")?.forEach((editorDom) => {
      bindEditorDom(editorDom);
      resolveEditorFromProseMirror(editorDom);
    });
  }

  function attachToShadowRoot(shadowRoot) {
    if (!shadowRoot || shadowRoot.__exiqLinkPatchShadowBound) {
      return;
    }

    shadowRoot.__exiqLinkPatchShadowBound = true;
    scanForEditorRoots(shadowRoot);
    bindBubbleMenuWatcher(shadowRoot, patchBubbleMenu);
    bindBubbleLinkButtonHandler(shadowRoot, openLinkForEditor);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          watchForLegacyLinkPopups(node);

          if (node.classList.contains("ProseMirror")) {
            bindEditorDom(node);
            resolveEditorFromProseMirror(node);
            return;
          }

          if (node.classList.contains("inline-bubble-inner") || node.classList.contains("inline-bubble")) {
            patchBubbleMenu(shadowRoot);
          }

          scanForEditorRoots(node);
        });
      });
    }).observe(shadowRoot, { childList: true, subtree: true });
  }

  function scanCustomElements(root) {
    root?.querySelectorAll?.("exiq-notion-editor")?.forEach((element) => {
      if (element.shadowRoot) {
        attachToShadowRoot(element.shadowRoot);
      }
    });
  }

  function observeEditors() {
    scanForEditorRoots(document);
    scanCustomElements(document);
    watchForLegacyLinkPopups(document);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }
          watchForLegacyLinkPopups(node);
          if (node.tagName === "EXIQ-NOTION-EDITOR" && node.shadowRoot) {
            attachToShadowRoot(node.shadowRoot);
          }
          if (node.classList.contains("ProseMirror")) {
            bindEditorDom(node);
          }
          scanForEditorRoots(node);
          scanCustomElements(node);
          if (node.shadowRoot) {
            attachToShadowRoot(node.shadowRoot);
          }
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observeEditors);
  } else {
    observeEditors();
  }
})();
